import { useMutation } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Grid, Typography } from '@material-ui/core'
import { Theme, makeStyles } from '@material-ui/core/styles'
import CheckCircle from '@material-ui/icons/CheckCircle'
import Skeleton from '@material-ui/lab/Skeleton'
import {
    CheckForDuplicateFilesInAssemblyInput,
    ImportUploadFileInfo,
} from 'models/projectImport'
import React from 'react'
import ElementsPanelImport from './ElementsPanelImport'

const useStyles = makeStyles((theme: Theme) => ({
    container: { paddingBottom: theme.spacing(3.125) },
    partitionGrid: {
        width: 485,
        height: 624,
    },
    partitionGridAssembly: {
        width: 485,
        height: 624,
        backgroundColor: '#f2f2f2',
        marginTop: theme.spacing(0.25),
        '& .resize-triggers, .resize-triggers > div, .contract-trigger:before': {
            position: 'unset',
            height: 550,
            width: 400,
        },
    },
    outerText: {
        fontSize: pxToRem(14),
        paddingTop: theme.spacing(8.5),
        marginBottom: theme.spacing(2),
    },
    familyNameText: {
        fontSize: pxToRem(24),
        color: '#211F20',
        marginTop: -theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    fullWidth: {
        width: '100%',
    },
    documentTitle: {
        color: '#5E5F61',
        textTransform: 'uppercase',
    },
    documentFileContainer: { height: 40, paddingLeft: theme.spacing(2) },
    documentFile: {
        ...theme.typography.h6,
    },
    noData: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        flexDirection: 'column',
        fontWeight: 'normal',
        '& div button.MuiButton-contained': {
            height: 30,
            width: 80,
            padding: 0,
        },
        '& .MuiButton-label': {
            fontSize: pxToRem(12),
        },
    },
    workspaceButton: {
        borderRadius: 100,
        fontSize: pxToRem(12),
        backgroundColor: theme.palette.common.white,
        '&:hover': {
            backgroundColor: '#33AEA6',
            color: theme.palette.common.white,
            boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
        },
    },

    uploadBtnContainer: {
        paddingRight: theme.spacing(0.5),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
    },
    iconButton: {
        height: 36,
        width: 34,
        padding: 0,
        margin: 0,
        '& .MuiIconButton-label': {
            height: '100%',
        },
        '& .MuiIcon-root': {
            height: 'unset',
            width: 'unset',
        },
    },
    withBorder: {
        borderBottom: '1px solid #04325B',
    },
    withoutBorder: {
        borderBottom: '1px solid #FFFFFF',
    },
    withTopPadding: {
        paddingTop: theme.spacing(1),
    },
    loadingCardContainer: {
        height: 45,
        width: 270,
        borderRadius: 10,
        border: '1px solid #3F3D56',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-evenly',
        marginBottom: theme.spacing(2.5),
    },

    skeletonContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    loadingSkeletonImport: {
        background: '#CCCCCC',
    },
    checkedCircleIcon: {
        height: 30,
        width: 30,
        color: '#6C63FF',
    },
    uncheckedCircle: {
        height: 30,
        width: 30,
        color: '#CCCCCC',
        backgroundColor: '#CCCCCC',
        borderRadius: 30,
    },
}))

interface SetAssociationsProps {
    selectedFile?: Partial<ImportUploadFileInfo> | null
    projectId?: string
    getAssociationLists?: (
        data: CheckForDuplicateFilesInAssemblyInput[],
        isPT?: boolean,
        removedElementTreePath?: string
    ) => void
    associations?: CheckForDuplicateFilesInAssemblyInput[]
    resetElements?: boolean
    handleResetElements?: (value: boolean) => void
    isBPImport?: boolean
}

const SetAssociations: React.FC<SetAssociationsProps> = ({
    selectedFile,
    projectId,
    getAssociationLists,
    associations,
    resetElements,
    handleResetElements,
    isBPImport,
}) => {
    const classes = useStyles()
    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)
    const loadingCard = React.useMemo(() => {
        return (
            <span className={classes.loadingCardContainer}>
                <span
                    data-testid="checkedCircleIcon"
                    className={classes.uncheckedCircle}
                />
                <span className={classes.skeletonContainer}>
                    <Skeleton
                        animation="pulse"
                        width={120}
                        height={7.5}
                        className={classes.loadingSkeletonImport}
                    />{' '}
                    <Skeleton
                        animation="pulse"
                        width={180}
                        height={7.5}
                        className={classes.loadingSkeletonImport}
                    />{' '}
                </span>
            </span>
        )
    }, [])
    return (
        <GridContainer
            data-testid="setAssociationDialog"
            className={classes.container}
        >
            <GridItem className={classes.uploadBtnContainer}></GridItem>
            <Grid className={classes.partitionGrid}>
                <Typography className={classes.outerText}>
                    Add Components under the intended Assemblies or Systems on
                    the Right by selecting tree elements. This will setup a
                    target for your Import.
                </Typography>
                <span className={classes.loadingCardContainer}>
                    <CheckCircle
                        data-testid="checkedCircleIcon"
                        className={classes.checkedCircleIcon}
                    />
                    <span className={classes.skeletonContainer}>
                        <Skeleton
                            animation="pulse"
                            width={120}
                            height={7.5}
                            className={classes.loadingSkeletonImport}
                        />{' '}
                        <Skeleton
                            animation="pulse"
                            width={180}
                            height={7.5}
                            className={classes.loadingSkeletonImport}
                        />{' '}
                    </span>
                </span>
                {loadingCard}
                {loadingCard}
                <Typography className={classes.outerText}>
                    Family Name
                </Typography>
                <Typography variant="h3" className={classes.familyNameText}>
                    {selectedFile?.title}
                </Typography>
            </Grid>
            <Grid
                className={classes.partitionGridAssembly}
                data-testid="elementsContainer"
            >
                <ElementsPanelImport
                    selectedFile={selectedFile}
                    projectId={projectId}
                    resetElements={resetElements}
                    getAssociationLists={getAssociationLists}
                    handleResetElements={handleResetElements}
                    showGlobalSnackbar={showGlobalSnackbar}
                    isBPImport={isBPImport}
                />
            </Grid>
        </GridContainer>
    )
}
export default SetAssociations
